import React, { useState, useEffect, useCallback } from 'react';
import { BackofficeLayout } from '../../layouts/BackofficeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Users, Check, X, Send, Clock, Loader2 } from 'lucide-react';
import { api } from '../../context/AuthContext';

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400' },
  approved: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400' },
  rejected: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400' },
  invited: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-400' },
};

function FeedbackSummary({ feedback }) {
  if (!feedback || feedback.total === 0) return null;
  const { summary } = feedback;
  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="feedback-summary">
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-[0.15em] text-slate-400">Beta Feedback</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-6">
          <div>
            <span className="text-lg font-mono font-bold text-white">{feedback.total}</span>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider ml-1">responses</span>
          </div>
          <div>
            <span className="text-lg font-mono font-bold text-green-400">{summary.very_useful}</span>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider ml-1">very useful</span>
          </div>
          <div>
            <span className="text-lg font-mono font-bold text-amber-400">{summary.somewhat_useful}</span>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider ml-1">somewhat</span>
          </div>
          <div>
            <span className="text-lg font-mono font-bold text-red-400">{summary.not_useful}</span>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider ml-1">not useful</span>
          </div>
          <div>
            <span className="text-lg font-mono font-bold text-orange-400">{summary.avg_score}</span>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider ml-1">avg score</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function BetaAdminPage() {
  const [applications, setApplications] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : '';
      const [appsResp, fbResp] = await Promise.all([
        api.get(`/backoffice/beta${params}`),
        api.get('/backoffice/beta/feedback'),
      ]);
      setApplications(appsResp.data.applications || []);
      setFeedback(fbResp.data);
    } catch { /* silent */ }
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAction = async (id, action) => {
    setActionLoading(`${id}-${action}`);
    try {
      await api.post(`/backoffice/beta/${id}/${action}`);
      toast.success(`Application ${action}d`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action}`);
    }
    setActionLoading(null);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '--';

  return (
    <BackofficeLayout>
      <div className="space-y-4" data-testid="beta-admin-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-orange-500" />
            <h1 className="text-base font-bold text-white tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>BETA APPLICATIONS</h1>
            <span className="text-xs text-slate-600 font-mono ml-2">{applications.length}</span>
          </div>
          <div className="flex gap-1">
            {['', 'pending', 'approved', 'rejected', 'invited'].map(s => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-sm transition-colors ${filter === s ? 'bg-slate-700 text-slate-100' : 'text-slate-500 hover:text-slate-300'}`}
                data-testid={`beta-filter-${s || 'all'}`}
              >
                {s || 'All'}
              </button>
            ))}
          </div>
        </div>

        <FeedbackSummary feedback={feedback} />

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-600" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-800">
                      <th className="text-left text-[10px] text-slate-500 uppercase tracking-wider py-2 px-2">Company</th>
                      <th className="text-left text-[10px] text-slate-500 uppercase tracking-wider py-2 px-2">Trade</th>
                      <th className="text-left text-[10px] text-slate-500 uppercase tracking-wider py-2 px-2">Email</th>
                      <th className="text-center text-[10px] text-slate-500 uppercase tracking-wider py-2 px-2">Estimators</th>
                      <th className="text-left text-[10px] text-slate-500 uppercase tracking-wider py-2 px-2">Tender Size</th>
                      <th className="text-center text-[10px] text-slate-500 uppercase tracking-wider py-2 px-2">Status</th>
                      <th className="text-left text-[10px] text-slate-500 uppercase tracking-wider py-2 px-2">Applied</th>
                      <th className="text-center text-[10px] text-slate-500 uppercase tracking-wider py-2 px-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map(app => {
                      const s = STATUS_STYLES[app.status] || STATUS_STYLES.pending;
                      return (
                        <tr key={app.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                          <td className="py-2 px-2 text-slate-200 font-medium">{app.company_name}</td>
                          <td className="py-2 px-2 text-[10px] font-mono text-slate-400 uppercase">{app.trade}</td>
                          <td className="py-2 px-2 text-slate-400">{app.email}</td>
                          <td className="py-2 px-2 text-center font-mono text-slate-300">{app.num_estimators}</td>
                          <td className="py-2 px-2 text-slate-400">{app.avg_tender_size || '--'}</td>
                          <td className="py-2 px-2 text-center">
                            <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-mono uppercase rounded-sm border ${s.bg} ${s.border} ${s.text}`}>{app.status}</span>
                          </td>
                          <td className="py-2 px-2 font-mono text-slate-500">{formatDate(app.created_at)}</td>
                          <td className="py-2 px-2 text-center">
                            <div className="flex justify-center gap-1">
                              {app.status === 'pending' && (
                                <>
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-green-400 hover:bg-green-500/10" onClick={() => handleAction(app.id, 'approve')} disabled={actionLoading === `${app.id}-approve`} data-testid={`approve-beta-${app.id}`}>
                                    {actionLoading === `${app.id}-approve` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:bg-red-500/10" onClick={() => handleAction(app.id, 'reject')} disabled={actionLoading === `${app.id}-reject`} data-testid={`reject-beta-${app.id}`}>
                                    {actionLoading === `${app.id}-reject` ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                                  </Button>
                                </>
                              )}
                              {app.status === 'approved' && (
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-cyan-400 hover:bg-cyan-500/10" onClick={() => handleAction(app.id, 'invite')} disabled={actionLoading === `${app.id}-invite`} data-testid={`invite-beta-${app.id}`}>
                                  {actionLoading === `${app.id}-invite` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                </Button>
                              )}
                              {(app.status === 'rejected' || app.status === 'invited') && (
                                <span className="text-[9px] text-slate-600"><Clock className="w-3 h-3 inline" /></span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {applications.length === 0 && (
                      <tr><td colSpan={8} className="py-8 text-center text-slate-600 text-xs">No applications yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackofficeLayout>
  );
}
